import React from "react";
import {
  DollarSign,
  Shield,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  FileText,
  Users,
} from "lucide-react";
import TopBar from "../components/TopBar";
import Footerr from "../components/Footerr/Footerr";
import { Link } from "react-router-dom";

const PricingPolicy = () => {
  const pricingPrinciples = [
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Market-Based Pricing",
      description:
        "Domain prices reflect current market conditions, brandability, length, and commercial value of .gold domains.",
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Transparent Pricing",
      description:
        "All prices are clearly displayed. No hidden fees, no surprises. What you see is what you pay.",
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Fair for Everyone",
      description:
        "Competitive pricing that benefits both domain sellers and buyers in the premium .gold marketplace.",
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Price Stability",
      description:
        "Listed prices remain valid until domain is sold or seller updates pricing. No sudden price changes.",
    },
  ];

  const pricingFactors = [
    {
      factor: "Domain Length",
      impact: "High Impact",
      description:
        "Shorter domains (1-5 characters) command premium prices due to memorability and scarcity.",
    },
    {
      factor: "Brandability",
      impact: "High Impact",
      description:
        "Pronounceable, memorable names with strong brand potential carry higher valuations.",
    },
    {
      factor: "Market Demand",
      impact: "High Impact",
      description:
        "Domains in trending industries or popular keywords reflect current market interest.",
    },
    {
      factor: "Dictionary Words",
      impact: "Medium-High Impact",
      description:
        "Common English words and industry terms are valued higher than random combinations.",
    },
    {
      factor: "Numbers & Hyphens",
      impact: "Medium Impact",
      description:
        "Pure letter domains typically valued higher than those containing numbers or hyphens.",
    },
    {
      factor: "Commercial Use",
      impact: "Medium Impact",
      description:
        "Business-relevant terms with clear commercial applications command better prices.",
    },
  ];

  const buyerGuidelines = [
    {
      title: "Purchase Process",
      points: [
        "Browse available .gold domains with clearly marked prices",
        "Submit purchase or make an offer on domains of interest",
        "Negotiate directly with sellers for best pricing",
        "Complete secure payment through our platform",
        "Receive domain transfer within 24-48 hours of payment",
      ],
    },
    {
      title: "Making Offers",
      points: [
        "You can make offers below the listed price",
        "Sellers may accept, reject, or counter your offer",
        "Be reasonable - lowball offers may be ignored",
        "Include a message explaining why you value the domain",
        "Response time varies by seller (typically 24-72 hours)",
      ],
    },
    {
      title: "Payment Terms",
      points: [
        "Full payment required before domain transfer",
        "Secure payment processing through trusted gateways",
        "Payment methods: Credit/Debit cards, Bank Transfer, Digital wallets",
        "All transactions in INR unless otherwise specified",
        "Receipt and invoice provided for all purchases",
      ],
    },
  ];

  const sellerGuidelines = [
    {
      title: "Setting Your Price",
      points: [
        "Research comparable .gold domain sales for reference",
        "Consider domain length, brandability, and market demand",
        "Price competitively to attract serious buyers",
        "You can update pricing anytime before sale",
        "Our team may provide pricing guidance upon request",
      ],
    },
    {
      title: "Offer Management",
      points: [
        "Review incoming offers within your seller dashboard",
        "Accept, reject, or counter offers as you see fit",
        "Respond promptly to maintain buyer interest",
        "Counter-offers can help negotiate favorable terms",
        "You're never obligated to accept below your minimum",
      ],
    },
    {
      title: "Payment Receipt",
      points: [
        "Receive payment within 24 hours of buyer payment",
        "Platform fee deducted from final sale price",
        "Payment via your preferred method (bank transfer, PayPal, etc.)",
        "Detailed transaction breakdown provided",
        "Tax documentation available for your records",
      ],
    },
  ];

  const fees = [
    {
      type: "Platform Fee",
      percentage: "10-15%",
      description:
        "Commission on successful sales, covers payment processing, domain transfer, and platform maintenance",
      appliesTo: "Sellers",
    },
    {
      type: "Listing Fee",
      percentage: "Free",
      description:
        "No upfront cost to list your .gold domains on our marketplace",
      appliesTo: "Sellers",
    },
    {
      type: "Buyer Fee",
      percentage: "Free",
      description:
        "No additional fees for buyers beyond the domain purchase price",
      appliesTo: "Buyers",
    },
    {
      type: "Transfer Fee",
      percentage: "Included",
      description:
        "Domain transfer costs included in platform fee, no extra charges",
      appliesTo: "All",
    },
  ];

  return (
    <div>
      <TopBar />

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-[#101c4c] to-[#1a2d6b] text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center p-3 bg-gradient-to-r from-[#fcdb6a] to-[#b99653] rounded-full mb-6">
                <DollarSign className="w-8 h-8 text-[#101c4c]" />
              </div>
              <h1 className="text-5xl font-bold mb-6 bg-gradient-to-r from-[#fcdb6a] to-[#b99653] bg-clip-text text-transparent">
                Pricing Policy
              </h1>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                Fair, transparent, and market-driven pricing for premium .gold
                domains. Understanding how we price excellence in the digital
                gold rush.
              </p>
            </div>
          </div>
        </div>

        {/* Pricing Principles */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#101c4c] mb-4">
              Our Pricing Principles
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Built on fairness, transparency, and market dynamics
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pricingPrinciples.map((principle, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-[#fcdb6a]"
              >
                <div className="w-12 h-12 bg-gradient-to-r from-[#fcdb6a] to-[#b99653] rounded-lg flex items-center justify-center mb-4 text-[#101c4c]">
                  {principle.icon}
                </div>
                <h3 className="text-xl font-bold text-[#101c4c] mb-2">
                  {principle.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {principle.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Pricing Factors */}
        <div className="bg-gradient-to-b from-gray-50 to-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#101c4c] mb-4">
                What Influences Domain Pricing?
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Multiple factors contribute to the valuation of premium .gold
                domains
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-[#101c4c] to-[#1a2d6b]">
                    <tr>
                      <th className="px-6 py-4 text-left text-white font-semibold">
                        Pricing Factor
                      </th>
                      <th className="px-6 py-4 text-left text-white font-semibold">
                        Impact Level
                      </th>
                      <th className="px-6 py-4 text-left text-white font-semibold">
                        Description
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {pricingFactors.map((item, index) => (
                      <tr
                        key={index}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-4 font-semibold text-[#101c4c]">
                          {item.factor}
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex px-3 py-1 rounded-full text-sm font-medium bg-gradient-to-r from-[#fcdb6a] to-[#b99653] text-[#101c4c]">
                            {item.impact}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-600">
                          {item.description}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Buyer Guidelines */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#101c4c] mb-4">
              For Buyers
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Everything you need to know about purchasing .gold domains
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {buyerGuidelines.map((section, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <h3 className="text-xl font-bold text-[#101c4c] mb-6 flex items-center gap-2">
                  <CheckCircle className="w-6 h-6 text-[#fcdb6a]" />
                  {section.title}
                </h3>
                <ul className="space-y-3">
                  {section.points.map((point, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-2 text-gray-600"
                    >
                      <span className="text-[#fcdb6a] mt-1">▸</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Seller Guidelines */}
        <div className="bg-gradient-to-b from-gray-50 to-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#101c4c] mb-4">
                For Sellers
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Maximize your returns with smart pricing and offer management
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {sellerGuidelines.map((section, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <h3 className="text-xl font-bold text-[#101c4c] mb-6 flex items-center gap-2">
                    <CheckCircle className="w-6 h-6 text-[#fcdb6a]" />
                    {section.title}
                  </h3>
                  <ul className="space-y-3">
                    {section.points.map((point, idx) => (
                      <li
                        key={idx}
                        className="flex items-start gap-2 text-gray-600"
                      >
                        <span className="text-[#fcdb6a] mt-1">▸</span>
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Fees Breakdown */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#101c4c] mb-4">
              Fee Structure
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Clear breakdown of all marketplace fees and charges
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {fees.map((fee, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 shadow-lg border-2 border-gray-100 hover:border-[#fcdb6a] transition-all duration-300"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-[#101c4c] mb-1">
                      {fee.type}
                    </h3>
                    <p className="text-sm text-gray-500">
                      Applies to: {fee.appliesTo}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold bg-gradient-to-r from-[#fcdb6a] to-[#b99653] bg-clip-text text-transparent">
                      {fee.percentage}
                    </div>
                  </div>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  {fee.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Important Notes */}
        <div className="bg-gradient-to-r from-[#101c4c] to-[#1a2d6b] py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <div className="flex items-start gap-4 mb-6">
                <AlertCircle className="w-8 h-8 text-[#fcdb6a] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-2xl font-bold text-white mb-4">
                    Important Notes
                  </h3>
                  <div className="space-y-3 text-gray-200">
                    <p className="flex items-start gap-2">
                      <span className="text-[#fcdb6a]">•</span>
                      <span>
                        All prices are in INR unless otherwise specified at the
                        time of listing.
                      </span>
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="text-[#fcdb6a]">•</span>
                      <span>
                        Domain ownership transfer fees and registrar transfer
                        costs may vary by registrar.
                      </span>
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="text-[#fcdb6a]">•</span>
                      <span>
                        Platform reserves the right to review and adjust pricing
                        for domains that violate marketplace guidelines.
                      </span>
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="text-[#fcdb6a]">•</span>
                      <span>
                        Sellers are responsible for ensuring domain transfer
                        readiness and unlocking domains for transfer.
                      </span>
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="text-[#fcdb6a]">•</span>
                      <span>
                        All transactions are subject to our Terms of Service and
                        domain transfer policies.
                      </span>
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="text-[#fcdb6a]">•</span>
                      <span>
                        Disputes regarding pricing or transactions should be
                        addressed through our support team within 7 days.
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-gradient-to-r from-[#101c4c] to-[#1a2d6b] rounded-2xl p-12 text-center shadow-2xl">
            <FileText className="w-16 h-16 text-[#fcdb6a] mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white mb-4">
              Questions About Pricing?
            </h2>
            <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
              Our team is here to help you understand domain valuations and make
              informed decisions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/marketplace-help">
                <button className="px-8 py-4 bg-gradient-to-r from-[#fcdb6a] to-[#b99653] text-[#101c4c] font-bold rounded-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                  Contact Support
                </button>
              </Link>
              <Link to="/Marketplace">
                <button className="px-8 py-4 bg-white/10 text-white font-semibold rounded-lg border-2 border-white/30 hover:bg-white/20 transition-all duration-300">
                  View Marketplace
                </button>
              </Link>
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="text-center text-gray-500 text-sm">
            <p className="mb-2">Last Updated: November 2025</p>
            <p>
              Brand.Gold reserves the right to update this pricing policy at any
              time. Changes will be communicated to active users via email.
            </p>
          </div>
        </div>
      </div>
      <Footerr />
    </div>
  );
};

export default PricingPolicy;
